import api from './api';

export interface HourlyRuleDto {
  id: number;
  ruleName: string;
  sqlQuery: string;
  runTimes: string;
  active: number | null;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHourlyRuleRequest {
  ruleName: string;
  sqlQuery: string;
  runTimes: string;
  active?: number;
}

export interface UpdateHourlyRuleRequest {
  ruleName: string;
  sqlQuery: string;
  runTimes: string;
  active?: number;
}

export interface RunHourlyRuleResponse {
  success: boolean;
  ruleId: number;
  ruleName: string;
  rowsAffected: number;
  message: string;
}

const toNum = (a: any, def = 0) => {
  const v = Number(a ?? def);
  return Number.isFinite(v) ? v : def;
};
const toStr = (a: any, def = '') => ((a ?? def) === null || (a ?? def) === undefined ? def : String(a ?? def));
const toBool = (a: any, def = false) => {
  if (a === true || a === 1 || a === 'true' || a === '1') return true;
  if (a === false || a === 0 || a === 'false' || a === '0') return false;
  return def;
};

const mapHourly = (r: any): HourlyRuleDto => ({
  id: toNum(r.id ?? r.Id, 0),
  ruleName: toStr(r.ruleName ?? r.RuleName),
  sqlQuery: toStr(r.sqlQuery ?? r.SqlQuery),
  runTimes: toStr(r.runTimes ?? r.RunTimes),
  active: (r.active ?? r.Active) === null || (r.active ?? r.Active) === undefined
    ? null
    : toNum(r.active ?? r.Active, 0),
  lastRunAt: r.lastRunAt ?? r.LastRunAt ?? null,
  createdAt: toStr(r.createdAt ?? r.CreatedAt),
  updatedAt: toStr(r.updatedAt ?? r.UpdatedAt)
});

const mapRunResponse = (d: any): RunHourlyRuleResponse => ({
  success: toBool(d.success ?? d.Success, false),
  ruleId: toNum(d.ruleId ?? d.RuleId, 0),
  ruleName: toStr(d.ruleName ?? d.RuleName),
  rowsAffected: toNum(d.rowsAffected ?? d.RowsAffected, 0),
  message: toStr(d.message ?? d.Message)
});

export const hourlyRulesService = {
  async getAll(): Promise<HourlyRuleDto[]> {
    const res = await api.get('/rules/hourly');
    return (res.data || []).map(mapHourly);
  },

  async getById(id: number): Promise<HourlyRuleDto> {
    const res = await api.get(`/rules/hourly/${id}`);
    return mapHourly(res.data);
  },

  async create(req: CreateHourlyRuleRequest): Promise<{ id: number; message: string }> {
    const res = await api.post('/rules/hourly', req);
    const d: any = res.data || {};
    return {
      id: toNum(d.id ?? d.Id, 0),
      message: toStr(d.message ?? d.Message)
    };
  },

  async update(id: number, req: UpdateHourlyRuleRequest): Promise<{ message: string }> {
    const res = await api.put(`/rules/hourly/${id}`, req);
    const d: any = res.data || {};
    return { message: toStr(d.message ?? d.Message) };
  },

  async remove(id: number): Promise<{ message: string }> {
    const res = await api.delete(`/rules/hourly/${id}`);
    const d: any = res.data || {};
    return { message: toStr(d.message ?? d.Message) };
  },

  async toggleActive(id: number, active: boolean): Promise<{ message: string; active: boolean }> {
    const res = await api.patch(`/rules/hourly/${id}/active`, { active });
    const d: any = res.data || {};
    return {
      message: toStr(d.message ?? d.Message),
      active: toBool(d.active ?? d.Active, active)
    };
  },

  async run(id: number): Promise<RunHourlyRuleResponse> {
    const res = await api.post(`/rules/hourly/${id}/run`);
    return mapRunResponse(res.data);
  }
};
